import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import type React from 'react';
import type {
  LocalizedContent,
  LocalizedLabel,
  NotificationCategory,
  NotificationLocale,
  NotificationSeverity,
} from '../types';

const CATEGORIES: NotificationCategory[] = [
  'SYSTEM',
  'SECURITY',
  'BILLING',
  'PRODUCT_UPDATE',
  'MARKETING',
];
const SEVERITIES: NotificationSeverity[] = ['INFO', 'WARNING', 'CRITICAL'];
const LOCALES: NotificationLocale[] = ['vi', 'en'];

interface ContentSectionProps {
  name: string;
  category: NotificationCategory;
  severity: NotificationSeverity;
  defaultLocale: NotificationLocale;
  activeLocale: NotificationLocale;
  content: LocalizedContent;
  emailEnabled: boolean;
  emailSubject: LocalizedLabel;
  ctaLabel: LocalizedLabel | null;
  ctaUrl: string;
  expiresAt: string;
  onNameChange: (value: string) => void;
  onCategoryChange: (value: NotificationCategory) => void;
  onSeverityChange: (value: NotificationSeverity) => void;
  onDefaultLocaleChange: (value: NotificationLocale) => void;
  onActiveLocaleChange: (value: NotificationLocale) => void;
  onContentChange: (locale: NotificationLocale, value: { title?: string; body?: string }) => void;
  onEmailSubjectChange: (locale: NotificationLocale, value: string) => void;
  onCtaLabelChange: (locale: NotificationLocale, value: string) => void;
  onCtaUrlChange: (value: string) => void;
  onExpiresAtChange: (value: string) => void;
}

export const ContentSection: React.FC<ContentSectionProps> = ({
  name,
  category,
  severity,
  defaultLocale,
  activeLocale,
  content,
  emailEnabled,
  emailSubject,
  ctaLabel,
  ctaUrl,
  expiresAt,
  onNameChange,
  onCategoryChange,
  onSeverityChange,
  onDefaultLocaleChange,
  onActiveLocaleChange,
  onContentChange,
  onEmailSubjectChange,
  onCtaLabelChange,
  onCtaUrlChange,
  onExpiresAtChange,
}) => {
  const localized = content[activeLocale] || { title: '', body: '' };
  const subject = emailSubject[activeLocale] || '';
  const cta = ctaLabel?.[activeLocale] || '';

  return (
    <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Content</h2>
      <div className="grid gap-3 md:grid-cols-3">
        <FormControl size="small">
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={category}
            onChange={(event) => onCategoryChange(event.target.value as NotificationCategory)}
          >
            {CATEGORIES.map((item) => (
              <MenuItem key={item} value={item}>
                {item.replace('_', ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small">
          <InputLabel>Severity</InputLabel>
          <Select
            label="Severity"
            value={severity}
            onChange={(event) => onSeverityChange(event.target.value as NotificationSeverity)}
          >
            {SEVERITIES.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Internal name"
          value={name}
          inputProps={{ maxLength: 120 }}
          onChange={(event) => onNameChange(event.target.value)}
          helperText={`${name.length}/120`}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={activeLocale}
          onChange={(_, next) => next && onActiveLocaleChange(next)}
        >
          {LOCALES.map((locale) => (
            <ToggleButton key={locale} value={locale}>
              {locale}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <FormControl size="small" className="min-w-40">
          <InputLabel>Default locale</InputLabel>
          <Select
            label="Default locale"
            value={defaultLocale}
            onChange={(event) => onDefaultLocaleChange(event.target.value as NotificationLocale)}
          >
            {LOCALES.map((locale) => (
              <MenuItem key={locale} value={locale}>
                {locale}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <TextField
        fullWidth
        label={`Title (${activeLocale})`}
        value={localized.title || ''}
        inputProps={{ maxLength: 120 }}
        onChange={(event) => onContentChange(activeLocale, { title: event.target.value })}
        helperText={`${(localized.title || '').length}/120`}
      />
      {emailEnabled && (
        <TextField
          fullWidth
          label={`Email subject (${activeLocale})`}
          value={subject}
          inputProps={{ maxLength: 150 }}
          onChange={(event) => onEmailSubjectChange(activeLocale, event.target.value)}
          helperText={`${subject.length}/150 · defaults to title`}
        />
      )}
      <TextField
        fullWidth
        multiline
        minRows={8}
        label={`Body markdown (${activeLocale})`}
        value={localized.body || ''}
        inputProps={{ maxLength: 5000 }}
        onChange={(event) => onContentChange(activeLocale, { body: event.target.value })}
        helperText={`${(localized.body || '').length}/5000`}
      />
      <div className="grid gap-3 md:grid-cols-[1fr_1.4fr]">
        <TextField
          label={`CTA label (${activeLocale})`}
          value={cta}
          inputProps={{ maxLength: 40 }}
          onChange={(event) => onCtaLabelChange(activeLocale, event.target.value)}
          helperText={`${cta.length}/40`}
        />
        <TextField
          label="CTA URL"
          value={ctaUrl}
          onChange={(event) => onCtaUrlChange(event.target.value)}
          placeholder="/subscription or https://example.com"
        />
      </div>
      <TextField
        type="datetime-local"
        label="Expires at"
        value={expiresAt}
        onChange={(event) => onExpiresAtChange(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    </section>
  );
};
